<template>
  <div class="min-h-screen flex flex-col">
    <AppNavbar :cart-count="cartCount" @open-cart="cartOpen = true" />

    <main class="flex-1">
      <HeroBanner />
      <TodayMenuSection @add-to-cart="addToCart" />
      <HowItWorks />
      <TestimonialsSection />
    </main>

    <AppFooter />

    <!-- Cart sidebar overlay -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="cartOpen"
        class="fixed inset-0 bg-accent/40 z-50 backdrop-blur-sm"
        @click.self="cartOpen = false"
      >
        <aside class="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-2xl flex flex-col">
          <div class="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-cream-200">
            <h2 class="font-display font-bold text-accent text-base sm:text-lg">
              Your Order
              <span v-if="cartCount" class="text-brand ml-1 text-base">({{ cartCount }})</span>
            </h2>
            <button class="p-2 rounded-xl hover:bg-cream-200 transition-colors" @click="cartOpen = false">
              <XMarkIcon class="w-5 h-5 text-accent" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">
            <div v-if="cartItems.length === 0" class="text-center py-16 text-accent/40">
              <p class="text-4xl mb-3">🛒</p>
              <p class="font-semibold">Your cart is empty</p>
              <p class="text-sm mt-1">Add dishes from today's menu</p>
            </div>

            <ul v-else class="flex flex-col gap-4">
              <li v-for="(entry, index) in cartItems" :key="entry.item.id" class="flex items-center gap-3">
                <div class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-cream-200 flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                  {{ entry.item.emoji || '🍽️' }}
                </div>
                <div class="flex-1 min-w-0">
                  <p class="font-semibold text-accent text-sm truncate">{{ entry.item.name }}</p>
                  <p class="text-xs text-accent/50">{{ formatPrice(entry.item.price) }} Rwf × {{ entry.qty }}</p>
                </div>
                <div class="flex items-center gap-1.5">
                  <button class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-cream-200 text-accent hover:bg-brand hover:text-white transition-colors text-xs sm:text-sm font-bold" @click="dec(index)">−</button>
                  <span class="w-5 text-center text-sm font-semibold text-accent">{{ entry.qty }}</span>
                  <button class="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-cream-200 text-accent hover:bg-brand hover:text-white transition-colors text-xs sm:text-sm font-bold" @click="inc(entry.item)">+</button>
                </div>
              </li>
            </ul>
          </div>

          <div v-if="cartItems.length > 0" class="px-4 py-3 sm:px-5 sm:py-4 border-t border-cream-200 space-y-3">
            <div class="flex justify-between font-display font-bold text-accent text-lg">
              <span>Total</span>
              <span class="text-brand">{{ formatPrice(cartTotal) }} Rwf</span>
            </div>
            <router-link
              to="/checkout"
              class="btn-primary w-full text-base py-3 text-center"
              @click="cartOpen = false"
            >
              Proceed to Checkout
            </router-link>
            <button class="w-full text-xs text-accent/40 hover:text-accent transition-colors py-1" @click="clearCart">
              Clear cart
            </button>
          </div>
        </aside>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useHead } from '@vueuse/head'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import AppNavbar from '@/components/layout/AppNavbar.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import HeroBanner from '@/components/home/HeroBanner.vue'
import TodayMenuSection from '@/components/home/TodayMenuSection.vue'
import HowItWorks from '@/components/home/HowItWorks.vue'
import TestimonialsSection from '@/components/home/TestimonialsSection.vue'
import { MenuItem } from '@/components/home/MenuCard.vue'

export default defineComponent({
  name: 'HomeView',
  components: { AppNavbar, AppFooter, HeroBanner, TodayMenuSection, HowItWorks, TestimonialsSection, XMarkIcon },
  setup () {
    useHead({
      title: 'HomeSweet Bites — Fresh Home-Cooked Meals Delivered in Kigali',
      meta: [
        { name: 'description', content: 'HomeSweet Bites delivers fresh, home-cooked Rwandan meals to your door daily in Kigali. Order via WhatsApp. Healthy, affordable, made with love.' },
        { property: 'og:title', content: 'HomeSweet Bites — Fresh Home-Cooked Meals Delivered in Kigali' },
        { property: 'og:type', content: 'website' }
      ]
    })

    const store = useStore()
    const cartOpen = ref(false)

    const cartItems = computed(() => store.getters['cart/items'])
    const cartCount = computed(() => store.getters['cart/count'])
    const cartTotal = computed(() => store.getters['cart/total'])

    const addToCart = (item: MenuItem) => {
      store.dispatch('cart/add', item)
      cartOpen.value = true
    }

    const inc = (item: MenuItem) => store.dispatch('cart/add', item)
    const dec = (index: number) => store.dispatch('cart/dec', index)
    const clearCart = () => store.dispatch('cart/clear')

    const formatPrice = (price: number) => price.toLocaleString('en-RW')

    return { cartOpen, cartItems, cartCount, cartTotal, addToCart, inc, dec, clearCart, formatPrice }
  }
})
</script>
